import { ArrowLeftIcon, DocumentTextIcon, MicrophoneIcon } from "@heroicons/react/24/outline";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import axios from "axios";
import { ReactNode, useEffect, useState } from "react";
import { extractCaseTitle, hasNarrative } from "../../utilities/caseContent";
import Button from "../common/Button";

// The case, read-only. It mirrors the viewers in the Files modal (PDF, image,
// text): the case's own text in the same editor the report is written in, but
// switched to read-only with no toolbar, no floating menu and none of the
// editing extras (proofreading, search, suggestions). A strip underneath lists
// the recordings whose transcripts a fill would also use.
export interface CaseDetail {
  id: string;
  caseNumber?: string | null;
  content?: { data?: string } | null;
}

export interface CaseRecording {
  id: string;
  file_name?: string | null;
  processing_status?: string | null;
  metadata?: { transcriptionUrl?: string; audioType?: string } | null;
}

// What a fill from this case has to work with.
export function caseReadiness(content: string | undefined, recordings: CaseRecording[]) {
  const waiting = recordings.filter((r) => ["pending", "uploading", "processing"].includes(r.processing_status || ""));
  const ready = recordings.filter((r) => r.processing_status === "completed" && r.metadata?.transcriptionUrl);
  const narrative = hasNarrative(content);
  let blocked: string | undefined;
  if (waiting.length) blocked = `"${waiting[0].file_name || "A recording"}" is still being transcribed.`;
  else if (!narrative && ready.length === 0) blocked = "This case has no narrative or finished recording to fill from yet.";
  return { narrative, ready: ready.length, waiting: waiting.length, blocked };
}

const extensions = [
  Color,
  Highlight.configure({ multicolor: true }),
  StarterKit,
  TextStyle,
  Table.configure({ resizable: false }),
  TableRow.extend({ content: "(tableHeader)*" }),
  TableHeader,
  TableCell,
  Underline,
];

const ReadOnlyCase: React.FC<{ html: string }> = ({ html }) => {
  const editor = useEditor({
    editable: false,
    extensions,
    content: html,
    editorProps: { attributes: { class: "prose dark:prose-invert max-w-none leading-[1.5] max-w-full" } },
  });
  useEffect(() => {
    if (editor && editor.getHTML() !== html) editor.commands.setContent(html, false);
  }, [editor, html]);
  return (
    <div className="editor [&_.ProseMirror_ul_>_li_>_p]:mb-0 [&_.ProseMirror_ul_>_li_>_p]:mt-0 [&_.ProseMirror_ol_>_li_>_p]:mb-0 [&_.ProseMirror_ol_>_li_>_p]:mt-0">
      <EditorContent editor={editor} />
    </div>
  );
};

export interface CaseDetailViewProps {
  caseId: string;
  onBack: () => void;
  // The host's action, given what a fill from this case can use (e.g. "Fill form with this case").
  actions: (info: { caseNumber?: string | null; blocked?: string }) => ReactNode;
}

const CaseDetailView: React.FC<CaseDetailViewProps> = ({ caseId, onBack, actions }) => {
  const [data, setData] = useState<{ case: CaseDetail; files: CaseRecording[] } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    axios
      .get(`/api/cases/${caseId}`)
      .then((res) => !cancelled && setData({ case: res.data.case, files: res.data.files || [] }))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const html = data?.case.content?.data || "";
  const readiness = data ? caseReadiness(html, data.files) : null;
  const title = html ? extractCaseTitle(html).replace(/<[^>]*>/g, "").trim() || "Case" : "Case";

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="font-semibold text-base-content truncate flex items-center gap-2 m-0 text-base">
            <DocumentTextIcon className="size-4 shrink-0" />
            <span className="truncate">{title}</span>
          </h1>
          <p className="text-xs text-base-content/60 m-0">
            {data?.case.caseNumber ? `Case ${data.case.caseNumber}` : "Case"}
            {readiness ? ` · ${readiness.ready} recording${readiness.ready === 1 ? "" : "s"} ready` : ""}
          </p>
        </div>
        {data && actions({ caseNumber: data.case.caseNumber, blocked: readiness?.blocked })}
      </div>

      <div className="flex-1 overflow-auto">
        <div className="mx-auto w-full px-6 py-6 max-w-4xl space-y-4">
          {error && <p className="text-base-content/70">This case could not be loaded.</p>}
          {!data && !error && <p className="text-base-content/60">Loading...</p>}
          {data && (
            <>
              {data.files.length > 0 && (
                <div className="rounded-xl border border-base-300 bg-base-200/50 px-4 py-3 text-sm">
                  <div className="text-[11px] uppercase tracking-wider text-base-content/50 mb-2">Recordings a fill will read</div>
                  <ul className="m-0 p-0 list-none space-y-1">
                    {data.files.map((f) => {
                      const done = f.processing_status === "completed" && f.metadata?.transcriptionUrl;
                      const failed = f.processing_status === "failed";
                      return (
                        <li key={f.id} className="flex items-center gap-2">
                          <MicrophoneIcon className="size-4 shrink-0 text-base-content/50" />
                          <span className="truncate flex-1">{f.file_name || "Recording"}</span>
                          <span
                            className={
                              done
                                ? "text-xs text-green-700 dark:text-green-400"
                                : failed
                                  ? "text-xs text-red-600 dark:text-red-400"
                                  : "text-xs text-amber-700 dark:text-amber-400"
                            }
                          >
                            {done ? "transcript ready" : failed ? "failed, skipped" : "transcribing"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {readiness?.blocked && <p className="text-sm text-amber-700 dark:text-amber-400 m-0">{readiness.blocked}</p>}
              <div className="rounded-xl border border-base-300 bg-base-100 px-6 py-4">
                {hasNarrative(html) ? <ReadOnlyCase html={html} /> : <p className="text-base-content/60 m-0">This case has no written narrative.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseDetailView;
